import { isDevBuild } from "@/config/env";
import { isRecord } from "@/lib/typeGuards";

/**
 * Where the credential lives when the app runs in a plain browser tab instead
 * of the native shell (see src/dev/DevLoginPage.tsx).
 *
 * `sessionStorage`, not a build variable: a `VITE_*` value is inlined into the
 * bundle and would ship with it, and changing it needs a dev-server restart.
 * This one is scoped to a single tab, disappears when the tab closes, and is
 * unreachable in a production build.
 */

export type DevAuthMode = | "bearer" | "session";

export interface DevAuth {
  token: string;
  mode: DevAuthMode;
}

const STORAGE_KEY = "sgc.devAuth";

function isDevAuth(value: unknown): value is DevAuth {
  return (
    isRecord(value) &&
    typeof value["token"] === "string" &&
    value["token"].length > 0 &&
    (value["mode"] === "bearer" || value["mode"] === "session")
  );
}

/** Returns null in a production build, and whenever nothing valid is stored. */
export function readDevAuth(): DevAuth | null {
  if (!isDevBuild) return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isDevAuth(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeDevAuth(auth: DevAuth): void {
  if (!isDevBuild) return;
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
}

export function clearDevAuth(): void {
  if (!isDevBuild) return;
  window.sessionStorage.removeItem(STORAGE_KEY);
}
